import React from 'react';
import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, Calendar, MapPin } from 'lucide-react';

const Experience: React.FC = () => {
  const workExperience = [
    {
      title: 'Technical Lead - AI Engineering',
      company: 'EY-GDS',
      location: 'Bengaluru, India',
      period: 'June 2026 - Present',
      description: 'Leading a 3-engineer team delivering enterprise-grade AI agents end-to-end.',
      achievements: [
        'Architected and delivered an enterprise-grade AI agent within ~3 months, reducing actuarial financial-statement review from 3-4 weeks to ~15 minutes (99%+ reduction) with 92%+ extraction accuracy',
        'Own the product lifecycle from stakeholder requirements and technical design through development and deployment, translating business needs into engineering scope',
        'Implemented Langfuse-based LLM observability to monitor agent traces, accuracy, latency, and failure cases in production'
      ]
    },
    {
      title: 'Software Engineer AI/ML',
      company: 'Quest Global',
      location: 'Bengaluru, India',
      period: 'September 2025 - April 2026',
      description: 'Built on-prem RAG systems and async AI microservices.',
      achievements: [
        'Built an on-prem LLM-based RAG system with reranking, reducing manual information retrieval time by ~40% and improving response accuracy',
        'Scaled API performance and lowered latency by ~30% by transitioning to async FastAPI microservices, with LangChain enabling efficient orchestration',
        'Automated ticket creation workflows, reducing manual effort by ~60% using an AI-powered travel booking agent built with Copilot Studio'
      ]
    },
    {
      title: 'Full Stack Software Engineer',
      company: 'SaayamForAll',
      location: 'New York City, USA',
      period: 'July 2024 - July 2025',
      description: 'Built fault-tolerant APIs and deployed LLM-powered services on AWS.',
      achievements: [
        'Cut infrastructure costs by 30% by building fault-tolerant APIs and deploying services on AWS Lambda and API Gateway',
        'Optimized response latency by deploying LLM-powered Flask chatbots on AWS Lambda for multilingual QA'
      ]
    },
    {
      title: 'Software Engineer',
      company: 'DXC Technology',
      location: 'Bengaluru, India',
      period: 'August 2020 - November 2021',
      description: 'Led ETL development and data pipeline optimization initiatives.',
      achievements: [
        'Enhanced data ingestion efficiency by streamlining ETL using Informatica and Airflow for multi-GB loads into Redshift',
        'Lowered pipeline runtime by 15% by improving scheduling and transformation logic across ETL jobs'
      ]
    }
  ];

  const education = [
    {
      degree: 'Master of Science - Computer Engineering',
      school: 'New York University',
      location: 'New York, US',
      period: 'September 2022 - May 2024',
      description: 'GPA: 3.83/4.0',
      courses: ['Machine Learning', 'Database Management', 'Big Data', 'Deep Learning', 'Operating Systems']
    },
    {
      degree: 'Bachelor of Technology - Information Technology',
      school: 'Vellore Institute of Technology',
      location: 'Vellore, India',
      period: 'July 2016 - June 2020',
      description: 'GPA: 8.62/10',
      courses: ['Data Structures', 'Algorithms', 'Software Testing', 'Object-Oriented Programming']
    }
  ];

  return (
    <section id="experience" className="section-padding">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Experience & Education
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            My professional journey and academic achievements
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Work Experience */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400 mr-4">
                <Briefcase size={24} />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Work Experience</h3>
            </div>

            <div className="space-y-8">
              {workExperience.map((job, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-dark-700 p-6 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600 card-hover"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{job.title}</h4>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">{job.company}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                        <Calendar size={16} className="mr-1" />
                        {job.period}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                        <MapPin size={16} className="mr-1" />
                        {job.location}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">{job.description}</p>
                  <ul className="space-y-2">
                    {job.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <div className="w-1.5 h-1.5 bg-primary-600 dark:bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-gray-600 dark:text-gray-300">{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Education */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center mb-8">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center text-primary-600 dark:text-primary-400 mr-4">
                <GraduationCap size={24} />
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Education</h3>
            </div>

            <div className="space-y-8">
              {education.map((edu, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-dark-700 p-6 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600 card-hover"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900 dark:text-white">{edu.degree}</h4>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">{edu.school}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-end">
                        <Calendar size={16} className="mr-1" />
                        {edu.period}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-end">
                        <MapPin size={16} className="mr-1" />
                        {edu.location}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 mb-3">{edu.description}</p>
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Relevant Courses:</p>
                    <div className="flex flex-wrap gap-2">
                      {edu.courses.map((course, courseIndex) => (
                        <span
                          key={courseIndex}
                          className="px-2 py-1 bg-gray-100 dark:bg-dark-600 text-gray-700 dark:text-gray-300 text-xs rounded"
                        >
                          {course}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Certifications */}
            <div className="mt-12">
              <h4 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Certifications</h4>
              <div className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-dark-700 p-6 rounded-lg shadow-sm border border-gray-100 dark:border-dark-600 card-hover"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="text-lg font-semibold text-gray-900 dark:text-white">Foundry & AIP Builder Foundations</h5>
                      <p className="text-primary-600 dark:text-primary-400 font-medium">Palantir Technologies</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                        <Calendar size={16} className="mr-1" />
                        June 2025
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
                    Comprehensive training in Palantir Foundry platform and AIP Builder foundations for enterprise data integration and AI-powered applications.
                  </p>
                  <a
                    href="https://verify.skilljar.com/c/8kej98tpu6xa"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium transition-colors"
                  >
                    Verify Certificate
                  </a>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Experience; 